'use client';

import Icon from '@/components/ui/AppIcon';

interface Booking {
  id: string;
  type: 'flight' | 'hotel' | 'holiday';
  destination: string;
  date: string;
  status: 'confirmed' | 'pending' | 'completed';
  price: number;
  pnr?: string;
}

const BookingsOverview = () => {
  const mockBookings: Booking[] = [
    {
      id: 'BK001',
      type: 'flight',
      destination: 'London to Paris',
      date: '2024-03-15',
      status: 'confirmed',
      price: 299,
      pnr: 'ABC123',
    },
    {
      id: 'BK002',
      type: 'hotel',
      destination: 'Hilton Paris Opera',
      date: '2024-03-15',
      status: 'confirmed',
      price: 450,
    },
    {
      id: 'BK003',
      type: 'holiday',
      destination: 'Bali Adventure Package',
      date: '2024-04-20',
      status: 'pending',
      price: 2199,
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-trust-green/10 text-trust-green border-trust-green/30';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/30';
      case 'completed':
        return 'bg-primary/10 text-primary border-primary/30';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'flight':
        return 'PaperAirplaneIcon';
      case 'hotel':
        return 'BuildingOffice2Icon';
      case 'holiday':
        return 'GlobeAltIcon';
      default:
        return 'TicketIcon';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20">
          <p className="text-sm text-muted-foreground mb-1">Active Bookings</p>
          <p className="text-3xl font-black text-primary">2</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-accent/10 to-accent/5 rounded-xl border border-accent/20">
          <p className="text-sm text-muted-foreground mb-1">Pending Bookings</p>
          <p className="text-3xl font-black text-accent">1</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-trust-green/10 to-trust-green/5 rounded-xl border border-trust-green/20">
          <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
          <p className="text-3xl font-black text-trust-green">£{mockBookings.reduce((sum, b) => sum + b.price, 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Bookings List */}
      <div className="space-y-4">
        <h3 className="text-xl font-black text-foreground flex items-center gap-2">
          <Icon name="TicketIcon" size={24} className="text-primary" />
          All Bookings
        </h3>
        {mockBookings.map((booking) => (
          <div
            key={booking.id}
            className="p-6 bg-white rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-200"
          >
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon name={getTypeIcon(booking.type) as any} size={28} className="text-white" />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h4 className="text-lg font-black text-foreground">{booking.destination}</h4>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(booking.status)}`}>
                    {booking.status.toUpperCase()}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="CalendarIcon" size={16} />
                    {new Date(booking.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {booking.pnr && (
                    <span className="flex items-center gap-1">
                      <Icon name="HashtagIcon" size={16} />
                      PNR: {booking.pnr}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Icon name="IdentificationIcon" size={16} />
                    ID: {booking.id}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-2xl font-black text-primary">£{booking.price}</p>
                  <p className="text-xs text-muted-foreground">Total Price</p>
                </div>
                <button className="px-4 py-2 bg-primary text-white font-bold rounded-lg hover:bg-secondary transition-all duration-200 shadow-md hover:shadow-lg">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookingsOverview;